import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
  Grid,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  BugReport as BugReportIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import { chatbotQuery } from '../services/api';

function DiagnosticTool({ selectedAccords }) {
  const [diagnosticResults, setDiagnosticResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const theme = useTheme();

  const addResult = (test, status, message, details = null) => {
    setDiagnosticResults(prev => [...prev, {
      test,
      status, // 'success', 'error', 'warning', 'info'
      message,
      details,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  const runDiagnostics = async () => {
    setIsRunning(true);
    setDiagnosticResults([]);
    
    try {
      // Test 1: Check if user has selected accords
      addResult(
        'Selected Accords Check',
        selectedAccords.length > 0 ? 'success' : 'warning',
        selectedAccords.length > 0 
          ? `✅ ${selectedAccords.length} accords selected`
          : '⚠️ No accords selected',
        selectedAccords.length > 0 ? {
          count: selectedAccords.length,
          sampleIds: selectedAccords.slice(0, 3).map(a => a.id),
          sampleObject: selectedAccords[0]
        } : null
      );

      // Test 2: Validate accord structure
      if (selectedAccords.length > 0) {
        const firstAccord = selectedAccords[0];
        const hasRequiredFields = firstAccord.id && 
                                 firstAccord.x !== undefined && 
                                 firstAccord.y !== undefined &&
                                 firstAccord.ementa;
        
        addResult(
          'Accord Structure Validation',
          hasRequiredFields ? 'success' : 'error',
          hasRequiredFields 
            ? '✅ Accord objects have required fields'
            : '❌ Accord objects missing required fields',
          {
            sampleStructure: Object.keys(firstAccord),
            hasId: !!firstAccord.id,
            hasCoordinates: firstAccord.x !== undefined && firstAccord.y !== undefined,
            hasContent: !!firstAccord.ementa
          }
        );

        // Test 3: Validate ID format
        const idFormat = firstAccord.id;
        const isValidHashFormat = typeof idFormat === 'string' && idFormat.length === 64;
        
        addResult(
          'ID Format Validation',
          isValidHashFormat ? 'success' : 'warning',
          isValidHashFormat 
            ? '✅ IDs appear to be valid hash format'
            : '⚠️ ID format may not be valid hash',
          {
            sampleId: idFormat,
            idLength: idFormat?.length,
            idType: typeof idFormat
          }
        );
      }

      // Test 4: Test API connectivity
      addResult('API Connectivity Test', 'info', '🔄 Testing backend connectivity...');
      
      try {
        const testResponse = await fetch('https://jurismap-backend-700321442947.southamerica-east1.run.app/API_plot_umap_embeddings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            advogados: [], recorrentes: [], relatores: [], resultados: [],
            period_start: null, period_end: null, titulo_keyword: "",
            ementa_keyword: "", ementa_search_mode: "Correspondência exata"
          }),
          credentials: 'include'
        });
        
        if (testResponse.ok) {
          const testData = await testResponse.json();
          addResult(
            'API Connectivity Test',
            'success',
            '✅ Backend API is accessible',
            {
              responseStatus: testResponse.status,
              dataReceived: testData.filtered_data_ids?.length || 0
            }
          );
        } else {
          addResult(
            'API Connectivity Test',
            'error',
            `❌ Backend API returned error: ${testResponse.status}`,
            { responseStatus: testResponse.status }
          );
        }
      } catch (apiError) {
        addResult(
          'API Connectivity Test',
          'error',
          '❌ Cannot connect to backend API',
          { error: apiError.message }
        );
      }

      // Test 5: Test Chatbot with selected accords (if any)
      if (selectedAccords.length > 0) {
        addResult('Chatbot Context Test', 'info', '🔄 Testing chatbot with selected accords...');
        
        try {
          const chatbotTestResponse = await chatbotQuery({
            user_message: "Liste os principais pontos dos casos selecionados para teste de diagnóstico.",
            conversation_history: [],
            filtered_data_ids: selectedAccords.map(accord => accord.id)
          });
          
          const responseContent = chatbotTestResponse.assistant_message;
          const hasValidResponse = !responseContent.includes('Não tenho dados disponíveis') &&
                                  !responseContent.includes('Não há casos disponíveis') &&
                                  !responseContent.includes('não há casos relevantes');
          
          addResult(
            'Chatbot Context Test',
            hasValidResponse ? 'success' : 'error',
            hasValidResponse 
              ? '✅ Chatbot successfully accessed selected cases'
              : '❌ Chatbot cannot access selected cases - Backend filter_data_by_id issue',
            {
              responsePreview: responseContent.substring(0, 200) + '...',
              responseLength: responseContent.length,
              hasContext: hasValidResponse
            }
          );
        } catch (chatbotError) {
          addResult(
            'Chatbot Context Test',
            'error',
            '❌ Chatbot test failed with error',
            { error: chatbotError.message }
          );
        }
      } else {
        addResult(
          'Chatbot Context Test',
          'info',
          'ℹ️ Skipped - No accords selected for testing'
        );
      }

      // Test 6: Session validation
      addResult('Session Check', 'info', '🔄 Checking session status...');
      
      try {
        // Test if session cookies are present
        const hasSessionCookies = document.cookie.includes('session') || document.cookie.length > 0;
        addResult(
          'Session Check',
          hasSessionCookies ? 'success' : 'warning',
          hasSessionCookies 
            ? '✅ Session cookies detected'
            : '⚠️ No session cookies found - may affect backend session continuity',
          { cookieInfo: document.cookie ? 'Present' : 'None' }
        );
      } catch (sessionError) {
        addResult(
          'Session Check',
          'warning',
          '⚠️ Could not check session status',
          { error: sessionError.message }
        );
      }

    } finally {
      setIsRunning(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'error': return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'warning': return <ErrorIcon sx={{ color: 'warning.main' }} />;
      default: return <InfoIcon sx={{ color: 'info.main' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'warning': return 'warning';
      default: return 'info';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <BugReportIcon sx={{ color: theme.palette.primary.main }} />
        Ferramenta de Diagnóstico JurisMap
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Esta ferramenta diagnostica problemas no fluxo de dados entre o frontend e backend,
        especificamente para verificar se os casos selecionados estão sendo corretamente
        passados para o chatbot e geração de defesa.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status Atual
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Chip 
                  label={`${selectedAccords.length} casos selecionados`}
                  color={selectedAccords.length > 0 ? 'success' : 'default'}
                  sx={{ mr: 1 }}
                />
                <Chip 
                  label={isRunning ? 'Executando testes...' : 'Pronto para testar'}
                  color={isRunning ? 'warning' : 'info'}
                />
              </Box>
              
              <Button
                variant="contained"
                startIcon={isRunning ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                onClick={runDiagnostics}
                disabled={isRunning}
                fullWidth
                sx={{ mt: 2 }}
              >
                {isRunning ? 'Executando Diagnóstico...' : 'Executar Diagnóstico Completo'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Casos Selecionados
              </Typography>
              {selectedAccords.length > 0 ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Primeiros 3 casos selecionados:
                  </Typography>
                  {selectedAccords.slice(0, 3).map((accord, index) => (
                    <Chip
                      key={index}
                      label={`${accord.id?.substring(0, 8)}...`}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhum caso selecionado. Vá para a página inicial e selecione alguns casos no mapa.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Resultados do Diagnóstico
              </Typography>
              
              {diagnosticResults.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Execute o diagnóstico para ver os resultados...
                </Typography>
              ) : (
                <List>
                  {diagnosticResults.map((result, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemIcon>
                          {getStatusIcon(result.status)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle2">
                                {result.test}
                              </Typography>
                              <Chip 
                                label={result.status.toUpperCase()} 
                                size="small"
                                color={getStatusColor(result.status)}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {result.timestamp}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                {result.message}
                              </Typography>
                              {result.details && (
                                <Paper sx={{ p: 1, mt: 1, bgcolor: 'grey.50' }}>
                                  <Typography variant="caption" component="pre">
                                    {JSON.stringify(result.details, null, 2)}
                                  </Typography>
                                </Paper>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < diagnosticResults.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default DiagnosticTool;